/**
 * URLParamsHandler
 * Parses and validates URL query parameters for section-based navigation.
 * Provides a consistent interface for extracting and applying URL state.
 *
 * @example
 * // In route handler:
 * const params = URLParamsHandler.parse(ctx.querystring, SessionsSection.urlParamSchema);
 * if (params.hasParams) {
 *   await section.applyURLParams(params.values);
 * }
 *
 * Schema format:
 * {
 *   paramName: {
 *     type: 'string' | 'boolean' | 'enum',
 *     enum?: string[],       // For enum type - allowed values
 *     default?: any,         // Default value if not provided
 *     validate?: (value) => boolean  // Optional custom validator
 *   }
 * }
 */
export class URLParamsHandler {
  /**
   * Parse query string into validated parameters based on schema
   * @param {string} querystring - Raw query string (without leading ?)
   * @param {Object} schema - Parameter schema defining allowed params and validators
   * @returns {{ hasParams: boolean, values: Object }} Parsed and validated params
   */
  static parse(querystring, schema) {
    if (!querystring || !schema) {
      return { hasParams: false, values: {} };
    }

    const searchParams = new URLSearchParams(querystring);
    const values = {};
    let hasParams = false;

    for (const [key, config] of Object.entries(schema)) {
      const rawValue = searchParams.get(key);

      if (rawValue !== null) {
        const validated = this._validateParam(rawValue, config);
        if (validated.valid) {
          values[key] = validated.value;
          hasParams = true;
        }
      }
    }

    return { hasParams, values };
  }

  /**
   * Build a query string from parameter values
   * @param {Object} values - Parameter values
   * @param {Object} schema - Parameter schema (for filtering valid params)
   * @returns {string} Query string (without leading ?)
   */
  static build(values, schema) {
    if (!values || !schema) {
      return '';
    }

    const params = new URLSearchParams();

    for (const [key, value] of Object.entries(values)) {
      if (schema[key] && value !== null && value !== undefined && value !== '') {
        // Skip default values to keep URLs clean
        if (schema[key].default === value) continue;
        // Skip false booleans (default state)
        if (schema[key].type === 'boolean' && value === false) continue;

        params.set(key, String(value));
      }
    }

    return params.toString();
  }

  /**
   * Validate a single parameter value against config
   * @param {string} value - Raw string value from URL
   * @param {Object} config - Parameter configuration
   * @returns {{ valid: boolean, value?: any }} Validation result with parsed value
   * @private
   */
  static _validateParam(value, config) {
    switch (config.type) {
      case 'boolean':
        return { valid: true, value: value === 'true' || value === '1' };

      case 'enum':
        if (config.enum && config.enum.includes(value)) {
          return { valid: true, value };
        }
        return { valid: false };

      case 'string':
      default:
        if (config.validate) {
          return config.validate(value) ? { valid: true, value } : { valid: false };
        }
        return { valid: true, value };
    }
  }
}
